/**
 * VerifiedReport — the polished, decision-ready presentation pattern used
 * for every published NTN Verify Hub verification report (Employment,
 * Landlord, Document Validation, Income, etc.).
 *
 * Read-only. Assembles a client-facing report from a published PM summary
 * payload — never touches internal findings, contact logs, timelines, or
 * claimed data. The backend `pm_summary` payload is the sole source of truth.
 *
 * Payload contract (produced by staff_ops `_build_<track>_pm_summary`):
 *   {
 *     status_banner: { tone: "green"|"amber"|"red", label: string },
 *     narrative:     string,
 *     bullets: [{ label, value?, verified?, claimed?, status }],
 *     employment_standing?: string[],     // optional per-track standings
 *     risk_indicators: [{ severity, title, description }],
 *     outcome_label, verification_method, verification_date,
 *     verification_completed_at,
 *     published_at, published_by_name,
 *   }
 *
 * Props:
 *   moduleName    : SMALL-CAPS eyebrow (e.g. "NTN Verify Hub · Employment Verification")
 *   title         : Report heading  (e.g. "Verified Employment Report")
 *   Icon          : lucide-react icon component for the header medallion
 *   payload       : the pm_summary payload described above
 *   testid        : root data-testid
 */
import { fmtDate, fmtDateLike } from "@/lib/dateFormat";
import {
  CheckCircle2, AlertTriangle, HelpCircle, Info, ShieldAlert, ShieldCheck,
} from "lucide-react";

// ── Tokens ───────────────────────────────────────────────────────────────
// Banner tones map 1:1 to the platform's Risk Severity Model.
//   green  → Verified · no material concerns
//   amber  → Verified with minor variances / inability to verify (Potential Risk)
//   orange → Material discrepancies warranting heightened scrutiny (Significant)
//   red    → Confirmed fraud / altered documents / identity issues (Elevated)
const BANNER_TONES = {
  green: {
    bg: "bg-emerald-700", border: "border-emerald-800",
    Icon: ShieldCheck, dot: "bg-emerald-300",
  },
  amber: {
    bg: "bg-amber-500", border: "border-amber-600",
    Icon: AlertTriangle, dot: "bg-amber-200",
  },
  orange: {
    bg: "bg-orange-600", border: "border-orange-700",
    Icon: AlertTriangle, dot: "bg-orange-200",
  },
  red: {
    bg: "bg-rose-700", border: "border-rose-800",
    Icon: ShieldAlert, dot: "bg-rose-300",
  },
  // Phase E.1: neutral pending state used when NTN staff has NOT yet
  // selected a manual banner. Never emitted by the deriver — only used
  // when the pm_summary status_banner comes back null.
  slate: {
    bg: "bg-slate-500", border: "border-slate-600",
    Icon: HelpCircle, dot: "bg-slate-300",
  },
};
const FIELD_STATUS = {
  verified:       { l: "Verified",        Icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 ring-emerald-200" },
  minor_variance: { l: "Minor Variance",  Icon: Info,         cls: "text-amber-800 bg-amber-50 ring-amber-200" },
  discrepancy:    { l: "Discrepancy",     Icon: AlertTriangle,cls: "text-rose-700 bg-rose-50 ring-rose-200" },
  unable:         { l: "Unable to Verify",Icon: HelpCircle,   cls: "text-slate-600 bg-slate-50 ring-slate-200" },
};
const RI_SEVERITY = {
  elevated:    { l: "Elevated",    cls: "text-rose-700 bg-rose-50 ring-rose-200",       Icon: AlertTriangle },
  significant: { l: "Significant", cls: "text-orange-800 bg-orange-50 ring-orange-200", Icon: AlertTriangle },
  potential:   { l: "Potential",   cls: "text-amber-800 bg-amber-50 ring-amber-200",    Icon: Info },
  info:        { l: "Info",        cls: "text-slate-700 bg-slate-50 ring-slate-200",    Icon: Info },
};

// ── Sub-components ───────────────────────────────────────────────────────
function ReportHeader({ moduleName, title, Icon }) {
  return (
    <header className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center gap-3">
      <div className="h-11 w-11 rounded-full bg-[#0A192F] flex items-center justify-center">
        {Icon ? <Icon className="h-5 w-5 text-white/90" /> : null}
      </div>
      <div className="min-w-0">
        <div className="ntn-eyebrow text-slate-500">{moduleName}</div>
        <h2 className="font-serif text-2xl leading-tight text-slate-900 mt-0.5">{title}</h2>
      </div>
    </header>
  );
}

function StatusBanner({ banner }) {
  // Phase E.1: when NTN staff hasn't set a manual risk yet, show a
  // neutral "Pending NTN Review" banner in slate. Never render a green
  // "Verified" as a fallback — the label must be human-selected.
  const b = banner || { tone: "slate", label: "Pending NTN Review" };
  const tone = BANNER_TONES[b.tone] || BANNER_TONES.slate;
  const Icon = tone.Icon;
  return (
    <div className={`${tone.bg} text-white px-6 py-4 flex items-center gap-3 border-y ${tone.border}`}
         data-testid="report-status-banner"
         data-tone={b.tone}>
      <div className="relative">
        <span className={`absolute inset-0 rounded-full ${tone.dot} opacity-30 animate-ping`} />
        <Icon className="relative h-6 w-6" />
      </div>
      <div className="font-serif text-xl leading-tight">{b.label}</div>
    </div>
  );
}

function VerifiedFieldsTable({ bullets, sectionLabel }) {
  if (!bullets?.length) return null;
  return (
    <section className="px-6 py-5">
      <div className="ntn-eyebrow text-slate-500 mb-3">{sectionLabel || "Verified Information"}</div>
      <dl className="divide-y divide-slate-100">
        {bullets.map((b) => {
          const status = FIELD_STATUS[b.status] || FIELD_STATUS.unable;
          const Icon = status.Icon;
          const value = fmtDateLike(b.value ?? b.verified ?? b.claimed ?? "—");
          return (
            <div key={b.label} className="grid grid-cols-[minmax(140px,180px)_1fr_auto] gap-4 py-3 items-center"
                 data-testid={`report-field-${b.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <dt className="text-xs uppercase tracking-wide text-slate-500 font-medium">{b.label}</dt>
              <dd className="text-slate-900 font-medium text-[15px]">{value}</dd>
              <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide px-2 py-1 rounded-full ring-1 ${status.cls}`}>
                <Icon className="h-3 w-3" /> {status.l}
              </span>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

function StandingChips({ standing }) {
  if (!standing?.length) return null;
  return (
    <div className="px-6 pb-4 flex flex-wrap gap-2" data-testid="report-standing">
      {standing.map((s) => (
        <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200">
          {s}
        </span>
      ))}
    </div>
  );
}

function RiskIntelligence({ indicators }) {
  if (!indicators?.length) return null;
  return (
    <section className="px-6 py-5 border-t border-slate-100 bg-slate-50/60" data-testid="report-risk-intelligence">
      <div className="ntn-eyebrow text-slate-500 mb-3">Risk Intelligence</div>
      <ul className="space-y-2.5">
        {indicators.map((ri, idx) => {
          const sev = RI_SEVERITY[ri.severity] || RI_SEVERITY.info;
          const Icon = sev.Icon;
          return (
            <li key={idx} className="bg-white rounded-md ring-1 ring-slate-200 px-3.5 py-2.5"
                data-testid={`report-ri-${idx}`}>
              <div className="flex items-start gap-3">
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${sev.cls} shrink-0 mt-0.5`}>
                  <Icon className="h-3 w-3" /> {sev.l}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 leading-snug">{ri.title}</div>
                  {ri.description ? (
                    <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{ri.description}</div>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function VerificationSummaryNarrative({ narrative }) {
  if (!narrative) return null;
  return (
    <section className="px-6 py-5 border-t border-slate-100" data-testid="report-narrative">
      <div className="ntn-eyebrow text-slate-500 mb-2">Verification Summary</div>
      <p className="text-[14px] leading-relaxed text-slate-800 font-serif">{narrative}</p>
    </section>
  );
}

function ReportFooter({ payload }) {
  const parts = [];
  if (payload?.verification_completed_at) {
    parts.push(`Verification Completed ${fmtDate(payload.verification_completed_at)}`);
  }
  if (payload?.verification_method) {
    parts.push(`Method: ${payload.verification_method.replace(/_/g, " ")}`);
  }
  const publishedLine = payload?.published_at
    ? `Published by ${payload.published_by_name || "NTN Staff"} · ${fmtDate(payload.published_at)}`
    : null;
  return (
    <footer className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between flex-wrap gap-2">
      <div>{parts.join(" · ") || " "}</div>
      {publishedLine ? <div>{publishedLine}</div> : null}
    </footer>
  );
}

// ── Main component ──────────────────────────────────────────────────────
export default function VerifiedReport({ moduleName, title, Icon, payload, testid, sectionLabel }) {
  if (!payload) return null;
  return (
    <section className="bg-white rounded-xl ring-1 ring-slate-200 shadow-sm overflow-hidden"
             data-testid={testid || "verified-report"}>
      <ReportHeader moduleName={moduleName} title={title} Icon={Icon} />
      <StatusBanner banner={payload.status_banner} />
      <VerifiedFieldsTable bullets={payload.bullets} sectionLabel={sectionLabel} />
      <StandingChips standing={payload.employment_standing} />
      <RiskIntelligence indicators={payload.risk_indicators} />
      <VerificationSummaryNarrative narrative={payload.narrative} />
      <ReportFooter payload={payload} />
    </section>
  );
}
